import {
  getHomeHeroMobileDisplaySize,
  getHomeHeroMobileN7ImageUrl,
} from '@/components/common/home/hero-banner-image-params'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { TBannerSlide } from '@/types/home'

type Props = {
  slides?: TBannerSlide[]
  label?: string
}

function getFirstVisibleSlide(slides?: TBannerSlide[]): TBannerSlide | null {
  if (!slides?.length) return null
  return slides.find((s) => !s.hidden) ?? null
}

/**
 * Static (non-interactive) hero banner renders the first visible slide without
 * any carousel JS. Next.js <Image> adds style="color:transparent" and
 * client-side hydration observers which delay LCP element paint by ~320ms.
 * A native <img> tag has zero client-side overhead → element render delay ≈ 0ms.
 *
 * Uses <picture> mobile-first: default <img src> is the mobile asset so phones
 * never speculatively request the desktop file.
 * URLs are direct N7 CDN (matches ReactDOM.preload on home / nam / nu).
 */
export function HeroBannerShell({ slides, label = 'Hero Banner' }: Props) {
  const firstSlide = getFirstVisibleSlide(slides)

  if (!firstSlide) return null

  const mobileUrl = getHomeHeroMobileN7ImageUrl(firstSlide.mb_image)
  // w-1440 covers common desktop; matches preload hint in page.tsx
  const desktopUrl = ImageHelper.getImageUrl(firstSlide.image, '', 1440)
  const altText = (firstSlide as { alt?: string }).alt ?? label

  // Mobile-first: default <img> = mobile so narrow viewports never speculatively
  // fetch desktopUrl (improves mobile LCP vs desktop-in-img fallback).
  const defaultSrc = mobileUrl || desktopUrl
  const { width: mobileW, height: mobileH } = getHomeHeroMobileDisplaySize()

  return (
    <section
      className='relative w-full max-sm:min-h-[500px] lg:min-h-[500px]'
      aria-label={label}
    >
      <picture className='block w-full'>
        {desktopUrl ? (
          <source media='(min-width: 769px)' srcSet={desktopUrl} />
        ) : null}
        <img
          src={defaultSrc}
          alt={altText}
          width={mobileW}
          height={mobileH}
          sizes='100vw'
          className='w-full object-cover'
          fetchPriority='high'
          loading='eager'
          decoding='async'
        />
      </picture>
    </section>
  )
}
